import {
  useBalances,
  useDisclose,
  useGetPageQueryParams,
  useHookForm,
  useMerchantApiMutation,
  useMerchantApiQueryWithParams,
  useNotifications,
  useQueryString,
} from "../../../../hooks";
import {
  IPageAndSearchRequest,
  PagedMeta,
  IAccessClaim,
  IViewTeamMember,
  IManageUserTransferLimit,
  IViewUserTransferLimit,
  IHookForm,
  IViewBalance,
} from "../../../../interfaces-and-types";

import { View } from "./View";
import { globalHandlerUtils } from "../../../../utils";
import { manageUserTransferLimitSchema } from "./validationSchema";
import {
  approvalRequiredTypes,
  approvalManagementTypes,
} from "../../../../enums";
import { useEffect, useState } from "react";
import { ITeamAndSecurityQueryMeta } from "../../TeamAndSecurity";
import { filterConstants } from "../../../../constants";
import { useFieldArray, UseFieldArrayReturn } from "react-hook-form";

export interface IManageTeamMemberTransferLimitsFormViewProps
  extends IHookForm<IManageUserTransferLimit> {
  // balances
  isLoadingBalances: boolean;
  balances: IViewBalance[];
  // limits
  limitCurrency: string;
  clearTeamMemberTransferLimit: () => void;
  // removeAccessClaimFromUser: () => void;
  isLoadingGetTransferLimitsByCurrency: boolean;
  isErrorGetTransferLimitsByCurrency: boolean;
  isSuccessGetTransferLimitsByCurrency: boolean;
  teamMemberTransferLimitsByCurrency: IViewUserTransferLimit | null;

  // access claims/roles
  isLoadingAvailableAccessClaims: boolean;
  isErrorAvailableAccessClaims: boolean;
  availableAccessClaims: IAccessClaim[];
  // team members
  isLoadingTeamMembers: boolean;
  isErrorTeamMembers: boolean;
  approverStaffListOptions: IViewTeamMember[];

  teamMember: IViewTeamMember | null;

  // form disclosure
  discloseLimitForm: ReturnType<typeof useDisclose>;

  // field arrays
  staffIdToAdd: string | undefined;
  setStaffIdToAdd: (claim: string) => void;
  roleToAdd: string | undefined;
  setRoleToAdd: (claim: string) => void;
  approverStaffListFieldArrayContext: UseFieldArrayReturn<
    IManageUserTransferLimit,
    "approver_staff_list_helper",
    "id"
  >;
  approverRolesListFieldArrayContext: UseFieldArrayReturn<
    IManageUserTransferLimit,
    "approver_roles_list_helper",
    "id"
  >;
  appendStaffToApproverList: () => void;
  appendRoleToApproverList: () => void;

  formValues: IManageUserTransferLimit;
}

export const ManageTeamMemberTransferLimits = (props: {
  teamMember: IViewTeamMember;
}) => {
  const query = useQueryString();
  const status = query.get(filterConstants.statusKey);
  const { page, page_size, search } = useGetPageQueryParams();

  const { isLoadingBalances, balances, refreshBalances } = useBalances();
  const notificationsHandler = useNotifications();

  const initialFormValues: IManageUserTransferLimit = {
    staff_id: props.teamMember.id!,
    currency: "NGN", // Default currency, can be changed based on your requirements
    description: "",

    // By approval requirement
    approval_count_required: 0, // Default to 1 approval required
    approval_required_type: approvalRequiredTypes.none, // Replace with a valid default value
    approval_management_type: approvalManagementTypes.role, // Replace with a valid default value
    approver_staff_list: [],
    approver_roles_list: [],
    approver_staff_list_helper: [],
    approver_roles_list_helper: [],

    // By volume
    single_transfer_amount_limit: null,
    daily_amount_limit: null,
    monthly_amount_limit: null,
    yearly_amount_limit: null,

    // By count
    daily_count_limit: null,
    monthly_count_limit: null,
    yearly_count_limit: null,

    // limit_deltas: [],
  };

  const {
    isLoading: isLoadingTeamMembers,
    isError: isErrorTeamMembers,
    data: teamMembersList,
    meta: teamMembersMeta,
    refetch: refetchTeamMembers,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest & ITeamAndSecurityQueryMeta,
    IViewTeamMember[],
    PagedMeta
  >([
    "getTeamMembers",
    {
      page: (page && Number(page)) || 1,
      page_size: (page_size && Number(page_size)) || 100,
      search,
      status: status || "active",
    },
  ]);

  const {
    isLoading: isLoadingAvailableAccessClaims,
    isError: isErrorAvailableAccessClaims,
    // isSuccess: isSuccessAvailableAccessClaims,
    data: availableAccessClaims,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest,
    IAccessClaim[],
    PagedMeta
  >([
    "getAvailableAccessClaims",
    {
      page: 1,
      page_size: 500,
      search: null,
    },
  ]);

  const {
    isSuccess: isSuccessSetTeamMemberTransferLimit,
    isLoading: isLoadingSetTeamMemberTransferLimit,
    mutate: setOrUpdateTeamMemberTransferLimit,
  } = useMerchantApiMutation<IManageUserTransferLimit, null>(
    "setTeamMemberTransferLimits",
    ["getUserTransferLimitByCurrency"],
    {},
    (urlTemplate) =>
      urlTemplate.replace("$team_member_id", props.teamMember.id!)
  ).usePostMutation;

  const handleApiSubmit = (data: IManageUserTransferLimit) => {
    setOrUpdateTeamMemberTransferLimit({
      ...data,
      approver_staff_list:
        data.approver_staff_list_helper?.map((item) => item.value) || [],
      approver_roles_list:
        data.approver_roles_list_helper?.map((item) => item.value) || [],
    });
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const { onFormSubmit, control, errors, register, reset, watch } = useHookForm(
    {
      initialFormValues,
      validationSchema: manageUserTransferLimitSchema,
      onValid: handleApiSubmit,
      onInvalid: handleError,
    }
  );

  const limitCurrency = watch("currency");

  const {
    isLoading: isLoadingGetTransferLimitsByCurrency,
    isError: isErrorGetTransferLimitsByCurrency,
    isSuccess: isSuccessGetTransferLimitsByCurrency,
    data: teamMemberTransferLimitsByCurrency,
  } = useMerchantApiQueryWithParams<
    { id: string; currency: string },
    IViewUserTransferLimit
  >(
    [
      "getUserTransferLimitByCurrency",
      {
        id: props.teamMember.id!,
        currency: limitCurrency,
      },
    ],
    {
      enabled: !!props.teamMember.id,
    },
    (urlTemplate) =>
      urlTemplate
        .replace("$team_member_id", props.teamMember.id!)
        .replace("$currency", limitCurrency),
    {
      hideResponseMessage: true,
      hideAxiosErrorMessages: true,
    }
  );

  const {
    isSuccess: isSuccessClearTeamMemberTransferLimit,
    isError: isErrorClearTeamMemberTransferLimit,
    isLoading: isLoadingClearTeamMemberTransferLimit,
    mutate: clearTeamMemberTransferLimit,
  } = useMerchantApiMutation<IManageUserTransferLimit, null>(
    "clearTeamMemberTransferLimits",
    ["getUserTransferLimitByCurrency"],
    {},
    (urlTemplate) =>
      urlTemplate
        .replace("$team_member_id", props.teamMember.id!)
        .replace("$currency", limitCurrency!)
  ).useDeleteMutation;

  const discloseLimitForm = useDisclose(false);

  useEffect(() => {
    if (isSuccessGetTransferLimitsByCurrency) {
      reset({
        ...teamMemberTransferLimitsByCurrency,
        approver_staff_list_helper:
          teamMemberTransferLimitsByCurrency?.approver_staff_list?.map(
            (staffId) => ({
              id: crypto.randomUUID(),
              value: staffId,
            })
          ) || [],
      });
    }
  }, [isSuccessGetTransferLimitsByCurrency, discloseLimitForm.isOpen]);

  useEffect(() => {
    if (isSuccessSetTeamMemberTransferLimit) {
      notificationsHandler.showNotification({
        ariaLabel: "error notification",
        title: "Transfer limit updated successfully",
        kind: "info",
        message:
          "The transfer limit has been updated successfully for the current team member.",
        statusIconDescription: "success",
        timeout: 10000,
      });

      setTimeout(() => discloseLimitForm.onClose(), 1000);
    }
  }, [isSuccessSetTeamMemberTransferLimit]);

  useEffect(() => {
    if (isSuccessClearTeamMemberTransferLimit) {
      reset(initialFormValues);
    }
  }, [isSuccessClearTeamMemberTransferLimit]);

  const [roleToAdd, setRoleToAdd] = useState<string>();
  const [staffIdToAdd, setStaffIdToAdd] = useState<string>();

  // APPROVER STAFF LIST FIELD ARRAY
  const approverStaffListFieldArrayContext = useFieldArray({
    name: "approver_staff_list_helper",
    control,
  });

  // APPROVER ROLES LIST FIELD ARRAY
  const approverRolesListFieldArrayContext = useFieldArray({
    name: "approver_roles_list_helper",
    control,
  });

  const appendStaffToApproverList = () => {
    // console.log("Appending staff to approver list:", staffIdToAdd);

    approverStaffListFieldArrayContext.append({
      id: crypto.randomUUID(),
      value: staffIdToAdd,
    });

    setStaffIdToAdd(undefined);
  };

  const appendRoleToApproverList = () => {
    // console.log("Appending role to approver list:", roleToAdd);

    approverRolesListFieldArrayContext.append({
      id: crypto.randomUUID(),
      value: roleToAdd,
    });

    setRoleToAdd(undefined);
  };

  if (errors) console.error("Form errors:", errors);

  return (
    <View
      // balances
      isLoadingBalances={isLoadingBalances}
      balances={balances}
      // limit
      limitCurrency={limitCurrency}
      isLoadingGetTransferLimitsByCurrency={
        isLoadingGetTransferLimitsByCurrency
      }
      isErrorGetTransferLimitsByCurrency={isErrorGetTransferLimitsByCurrency}
      isSuccessGetTransferLimitsByCurrency={
        isSuccessGetTransferLimitsByCurrency
      }
      teamMemberTransferLimitsByCurrency={
        teamMemberTransferLimitsByCurrency || null
      }
      // set/update
      clearTeamMemberTransferLimit={clearTeamMemberTransferLimit}
      // access claims for approval settings
      isLoadingAvailableAccessClaims={isLoadingAvailableAccessClaims}
      isErrorAvailableAccessClaims={isErrorAvailableAccessClaims}
      availableAccessClaims={availableAccessClaims || []}
      // team members
      isLoadingTeamMembers={isLoadingTeamMembers}
      isErrorTeamMembers={isErrorTeamMembers}
      approverStaffListOptions={
        teamMembersList?.filter((mem) => mem.id !== props.teamMember.id) || []
      }
      // user
      teamMember={props.teamMember}
      // form disclosure
      discloseLimitForm={discloseLimitForm}
      // field arrays
      staffIdToAdd={staffIdToAdd}
      setStaffIdToAdd={setStaffIdToAdd}
      roleToAdd={roleToAdd}
      setRoleToAdd={setRoleToAdd}
      approverStaffListFieldArrayContext={approverStaffListFieldArrayContext}
      approverRolesListFieldArrayContext={approverRolesListFieldArrayContext}
      appendStaffToApproverList={appendStaffToApproverList}
      appendRoleToApproverList={appendRoleToApproverList}
      // form values
      formValues={watch()}
      // form
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
    />
  );
};
